import React, { useMemo, useRef, useState } from 'react';
import { AuthorizedView, Roles } from '../../Utilities/AuthorizedView';
import AGTAbleWrapper from 'components/AGTable/AGTableWrapper';
import AGMessage from 'components/AGMessage/AGMessage';
import IconButton from 'components/Button/IconButton';
import TrashIcon from 'assets/icons/trash.svg?react';
import InvitationForm from '../InvitationForm/InvitationForm';
import { useTeam } from 'contexts/TeamContext';
import { Organization, OrganizationMember } from 'types';
import { Invite } from 'types/Invite';
import { AppState } from 'reducers/reducers';
import { connect } from 'react-redux';
import Modal, { ModalRef } from 'components/Modal/Modal';
import ConfirmAction from 'components/ConfirmAction/ConfirmAction';

type TeamMember = Pick<Invite, 'pk' | 'email' | 'role'> & {
    name: string;
    remove: () => void;
    status: 'Active' | 'Invited';
};

function byNameComparator(a: { name: string }, b: { name: string }) {
    return a.name.localeCompare(b.name);
}

type TeamModuleProps = ReturnType<typeof mapStateToProps> & {
    organization: Organization;
};

function TeamModule(props: TeamModuleProps) {
    const { team, deleteTeamMemberInvite, deleteTeamMember, successMessage, errorMessage, loading } = useTeam();
    const { profile, organization } = props;

    const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
    const confirmDeleteMemberModal = useRef<ModalRef>();

    /**
     * All invites should also have memberships for the same user (though not guaranteed)
     * If both exist or just an invite exists, we'll show the member's Invite status (altered so `Active` becomes `Invited`),
     * Otherwise we'll show their membership with the status `Active`.
     */
    const teamMembers: TeamMember[] = useMemo(() => {
        const invitesByEmail: Record<string, Invite> = Object.fromEntries(
            team?.invites.map((invite) => [invite.email, invite]) || []
        );
        const inviteEmails = new Set(Object.keys(invitesByEmail));
        const memberHasInvite = (organizationMember: OrganizationMember) =>
            organizationMember.email && inviteEmails.has(organizationMember.email);
        const memberHasLoggedIn = (organizationMember: OrganizationMember) => organizationMember.last_login !== null;

        const removeMember = (organizationMember: OrganizationMember) => {
            if (memberHasInvite(organizationMember)) {
                // If the member has an invite too, we'll delete the invite. The backend cascades the member deletion.
                deleteTeamMemberInvite(invitesByEmail[organizationMember.email as string].pk, organization.pk);
            } else {
                deleteTeamMember(organizationMember.pk, organization.pk);
            }
        };

        const orgMembers =
            team?.organizationMembers
                .filter(memberHasLoggedIn)
                .map((organizationMember) => ({
                    name: `${organizationMember.first_name} ${organizationMember.last_name}`,
                    pk: organizationMember.pk,
                    email: organizationMember.email || '',
                    role: organizationMember.role,
                    status: 'Active' as const,
                    remove: () => removeMember(organizationMember),
                }))
                .sort(byNameComparator) || [];

        const activeMemberEmails = new Set(orgMembers.map((member) => member.email));
        const inviteIsNotActiveMember = (invite: Invite) => !activeMemberEmails.has(invite.email);
        const removeInvite = (invite: Invite) => deleteTeamMemberInvite(invite.pk, organization.pk);

        const invites =
            team?.invites
                .filter(inviteIsNotActiveMember)
                .map((invite) => ({
                    name: `${invite.first_name} ${invite.last_name}`,
                    pk: invite.pk,
                    email: invite.email,
                    role: invite.role,
                    status: 'Invited' as const,
                    remove: () => removeInvite(invite),
                }))
                .sort(byNameComparator) || [];

        return [...invites, ...orgMembers];
    }, [team, deleteTeamMemberInvite, deleteTeamMember, organization.pk]);

    function showConfirmRemoveModal(member: TeamMember) {
        setSelectedMember(member);
        confirmDeleteMemberModal.current?.openModal();
    }

    function closeConfirmRemoveModal() {
        setSelectedMember(null);
        confirmDeleteMemberModal.current?.closeModal();
    }

    return (
        <div>
            <div style={{ marginBottom: '20px' }}>
                {successMessage && <AGMessage color="success">{successMessage}</AGMessage>}
                {errorMessage && <AGMessage color="error">{errorMessage}</AGMessage>}

                <AuthorizedView allowed={Roles.Admin}>
                    <InvitationForm orgId={organization.pk} />
                </AuthorizedView>
            </div>
            <div>
                {teamMembers.length > 0 ? (
                    <AGTAbleWrapper>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Status</th>
                                <AuthorizedView allowed={Roles.Admin}>
                                    <th>Remove</th>
                                </AuthorizedView>
                            </tr>
                        </thead>
                        <tbody>
                            {teamMembers.map((member, index) => {
                                return (
                                    <tr key={index}>
                                        <td>{member.name}</td>
                                        <td>{member.email}</td>
                                        <td>{member.role}</td>
                                        <td>{member.status}</td>
                                        <AuthorizedView allowed={Roles.Admin}>
                                            <td>
                                                {member.email !== profile.email && (
                                                    <IconButton
                                                        disabled={loading}
                                                        onClick={() => showConfirmRemoveModal(member)}
                                                    >
                                                        <TrashIcon width={12} fill="var(--ag-error)" />
                                                    </IconButton>
                                                )}
                                            </td>
                                        </AuthorizedView>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </AGTAbleWrapper>
                ) : (
                    <p>No team members.</p>
                )}
            </div>

            {/* Confirm Delete Modal */}
            <Modal ref={confirmDeleteMemberModal} title={`Are you sure you want to remove ${selectedMember?.name}?`}>
                <ConfirmAction
                    onCancel={closeConfirmRemoveModal}
                    onConfirm={() => selectedMember?.remove()}
                    action="Remove"
                    loading={loading}
                />
            </Modal>
        </div>
    );
}

function mapStateToProps(state: AppState) {
    return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        profile: state.auth.profile!,
    };
}

export default connect(mapStateToProps)(TeamModule);
